import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import UniversityManagerLayout from "../../components/layout/UniversityManagerLayout";
import UMTransferList from "../../features/university-manager/UMTransferList";
import AppButton from "../../components/ui/AppButton";
import { useTenant } from "../../context/TenantContext";
import umService from "../../services/university-admin/umService";
import { MOCK_CLUBS } from "../../mock/mockData";

const UMFinanceTransfers = () => {
  const { t } = useTranslation("universityManager");
  const { t: tCommon } = useTranslation("common");
  const { userRole, currentTenantId } = useTenant();
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({ dateFrom: "", dateTo: "", type: "", status: "", transferType: "", clubId: "" });

  const clubs = userRole === "UNIVERSITY_MANAGER" && currentTenantId
    ? MOCK_CLUBS.filter((c) => c.universityId === currentTenantId)
    : MOCK_CLUBS;

  const fetchTransfers = useCallback(async () => {
    setLoading(true);
    const res = await umService.getTransfers({ userRole, currentTenantId, ...Object.fromEntries(Object.entries(filters).map(([k, v]) => [k, v || undefined])), page: currentPage, size: pageSize });
    if (res.success) {
      setTransfers(res.data.content);
      setTotalPages(res.data.totalPages);
      setTotalElements(res.data.totalElements);
    }
    setLoading(false);
  }, [userRole, currentTenantId, filters, currentPage, pageSize]);

  useEffect(() => { fetchTransfers(); }, [fetchTransfers]);

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pages = [];
    const startPage = Math.max(0, currentPage - 2);
    const endPage = Math.min(totalPages - 1, currentPage + 2);
    for (let i = startPage; i <= endPage; i++) {
      pages.push(<AppButton key={i} variant={i === currentPage ? "primary" : "outline"} className="btn-sm mx-1" onClick={() => setCurrentPage(i)}>{i + 1}</AppButton>);
    }
    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="text-muted">{tCommon("pagination.showingEntries", { start: currentPage * pageSize + 1, end: Math.min((currentPage + 1) * pageSize, totalElements), total: totalElements })}</div>
        <div className="d-flex align-items-center">
          <AppButton variant="outline" className="btn-sm me-2" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 0}>{tCommon("buttons.previous")}</AppButton>
          {pages}
          <AppButton variant="outline" className="btn-sm ms-2" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages - 1}>{tCommon("buttons.next")}</AppButton>
        </div>
      </div>
    );
  };

  return (
    <UniversityManagerLayout>
      <div className="finance-transfers-page">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="h3 mb-1 fw-bold text-dark">
              <i className="fas fa-exchange-alt me-2 text-success"></i>
              {t("finance.transfers.title")}
            </h1>
            <p className="text-muted mb-0">{t("finance.transfers.subtitle")}</p>
          </div>
          <div className="d-flex align-items-center gap-2">
            <label className="form-label mb-0">{t("finance.transfers.show")}:</label>
            <select className="form-select form-select-sm" value={pageSize} onChange={(e) => { setPageSize(parseInt(e.target.value)); setCurrentPage(0); }} style={{ width: "auto" }}>
              <option value={5}>5</option><option value={10}>10</option><option value={25}>25</option><option value={50}>50</option>
            </select>
          </div>
        </div>
        <UMTransferList transfers={transfers} loading={loading} totalElements={totalElements} clubs={clubs} filters={filters} onFilterChange={(key, value) => { setFilters((f) => ({ ...f, [key]: value })); setCurrentPage(0); }} onClearFilters={() => { setFilters({ dateFrom: "", dateTo: "", type: "", status: "", transferType: "", clubId: "" }); setCurrentPage(0); }} />
        {renderPagination()}
      </div>
    </UniversityManagerLayout>
  );
};

export default UMFinanceTransfers;
